import { alpha, createTheme, type ThemeOptions } from '@mui/material/styles';
import {
  APP_ACTION,
  APP_BORDERS,
  APP_BRAND,
  APP_CSS_VARS,
  APP_SEMANTIC,
  APP_SHAPE,
  APP_SURFACES,
  APP_TEXT_COLOR,
  type AppColorMode,
} from '../constants/colors';
import { appTypography, appTypographyComponents } from '../constants/typography';
import { APP_FONT_FAMILY } from '../constants/fonts';

export function createAppTheme(mode: AppColorMode, direction: 'ltr' | 'rtl' = 'ltr') {
  const surfaces = APP_SURFACES[mode];
  const borders = APP_BORDERS[mode];
  const action = APP_ACTION[mode];
  const isDark = mode === 'dark';

  const palette = {
    mode,
    primary: { ...APP_BRAND.primary },
    secondary: { ...APP_BRAND.secondary },
    success: { ...APP_SEMANTIC.success },
    error: { ...APP_SEMANTIC.error },
    warning: { ...APP_SEMANTIC.warning },
    info: { ...APP_SEMANTIC.info },
    background: {
      default: surfaces.default,
      paper: surfaces.paper,
      elevated: surfaces.elevated,
      input: surfaces.input,
      inputFocused: surfaces.inputFocused,
      shell: surfaces.shell,
    },
    text: {
      primary: APP_TEXT_COLOR[mode].primary,
      secondary: APP_TEXT_COLOR[mode].secondary,
      disabled: APP_TEXT_COLOR[mode].disabled,
    },
    divider: borders.default,
    action: {
      hover: action.hover,
      selected: action.selected,
      focus: action.focusRing,
    },
  } as const;

  const cardShadow = isDark
    ? '0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.35)'
    : '0 1px 3px rgba(26, 35, 50, 0.07), 0 1px 2px rgba(26, 35, 50, 0.04)';

  const cardHoverShadow = isDark
    ? '0 8px 28px rgba(0, 0, 0, 0.55)'
    : '0 8px 24px rgba(26, 35, 50, 0.10)';

  const themeOptions: ThemeOptions = {
    direction,
    typography: appTypography,
    shape: {
      borderRadius: APP_SHAPE.radiusMd,
    },
    palette,
    components: {
      ...appTypographyComponents,
      MuiCssBaseline: {
        styleOverrides: {
          ...(appTypographyComponents?.MuiCssBaseline?.styleOverrides as object),
          ':root': {
            '--ops-primary': APP_CSS_VARS.primary,
            '--ops-primary-rgb': APP_CSS_VARS.primaryRgb,
            '--ops-bg-default': surfaces.default,
            '--ops-bg-paper': surfaces.paper,
            '--ops-text-primary': APP_TEXT_COLOR[mode].primary,
            '--ops-text-secondary': APP_TEXT_COLOR[mode].secondary,
            '--ops-border': borders.default,
            '--app-font-family': APP_FONT_FAMILY,
          },
          body: {
            fontFamily: APP_FONT_FAMILY,
            backgroundColor: surfaces.default,
            color: APP_TEXT_COLOR[mode].primary,
          },
          '::-webkit-scrollbar-thumb': {
            background: alpha(APP_BRAND.primary.main, isDark ? 0.32 : 0.22),
            borderRadius: 4,
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: alpha(APP_BRAND.primary.main, isDark ? 0.46 : 0.34),
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
            borderRadius: APP_SHAPE.radiusMd,
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            boxShadow: 'none',
            backgroundColor: APP_BRAND.primary.main,
            '&:hover': {
              backgroundColor: APP_BRAND.primary.dark,
              boxShadow: `0 4px 16px ${alpha(APP_BRAND.primary.main, 0.35)}`,
            },
          },
          outlined: {
            borderColor: borders.strong,
            '&:hover': {
              borderColor: APP_BRAND.primary.main,
              backgroundColor: alpha(APP_BRAND.primary.main, isDark ? 0.08 : 0.06),
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: APP_SHAPE.radiusLg,
            backgroundColor: surfaces.paper,
            border: `1px solid ${borders.subtle}`,
            boxShadow: cardShadow,
            transition: 'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: cardHoverShadow,
              borderColor: borders.default,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: APP_SHAPE.radiusMd,
            backgroundImage: 'none',
            backgroundColor: surfaces.paper,
          },
          outlined: {
            borderColor: borders.default,
          },
          elevation0: {
            boxShadow: 'none',
          },
          elevation1: {
            border: 'none',
            boxShadow: isDark
              ? '0 1px 2px rgba(0, 0, 0, 0.35)'
              : '0 1px 2px rgba(26, 35, 50, 0.06)',
          },
          elevation2: {
            border: 'none',
            boxShadow: isDark
              ? '0 2px 8px rgba(0, 0, 0, 0.4)'
              : '0 2px 8px rgba(26, 35, 50, 0.08)',
          },
          elevation3: {
            border: 'none',
          },
          elevation4: {
            border: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            backgroundColor: surfaces.shell,
            color: APP_TEXT_COLOR[mode].primary,
            borderBottom: `1px solid ${borders.default}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: surfaces.shell,
            borderColor: borders.default,
            borderRight: `1px solid ${borders.default}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: APP_SHAPE.radiusMd,
            backgroundColor: surfaces.input,
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: borders.strong,
            },
            '&.Mui-focused': {
              backgroundColor: surfaces.inputFocused,
              boxShadow: `0 0 0 3px ${action.focusRing}`,
            },
          },
          notchedOutline: {
            borderColor: borders.default,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: `${APP_SHAPE.radiusMd}px`,
          },
          outlined: {
            borderColor: borders.strong,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily: APP_FONT_FAMILY,
            fontSize: '1rem',
            lineHeight: 1.43,
            borderColor: borders.subtle,
          },
          head: {
            fontSize: '0.9375rem',
            fontWeight: 600,
            textTransform: 'none',
            letterSpacing: '0.01em',
            backgroundColor: isDark ? alpha(surfaces.elevated, 0.6) : surfaces.elevated,
            color: APP_TEXT_COLOR[mode].secondary,
            borderBottom: `2px solid ${borders.default}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.15s ease',
            '&:last-child td, &:last-child th': {
              borderBottom: 0,
            },
          },
          hover: {
            '&:hover': {
              backgroundColor: alpha(APP_BRAND.primary.main, isDark ? 0.1 : 0.06),
            },
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            '& .MuiTableBody-root .MuiTableRow-root:hover': {
              backgroundColor: alpha(APP_BRAND.primary.main, isDark ? 0.1 : 0.06),
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: borders.default,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: APP_SHAPE.radiusLg,
            border: `1px solid ${borders.default}`,
            boxShadow: isDark
              ? '0 24px 48px rgba(0, 0, 0, 0.55)'
              : '0 24px 48px rgba(26, 35, 50, 0.12)',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardSuccess: {
            backgroundColor: alpha(APP_SEMANTIC.success.main, isDark ? 0.14 : 0.10),
            color: isDark ? APP_SEMANTIC.success.light : APP_SEMANTIC.success.dark,
          },
          standardError: {
            backgroundColor: alpha(APP_SEMANTIC.error.main, isDark ? 0.14 : 0.10),
            color: isDark ? APP_SEMANTIC.error.light : APP_SEMANTIC.error.dark,
          },
          standardWarning: {
            backgroundColor: alpha(APP_SEMANTIC.warning.main, isDark ? 0.14 : 0.10),
            color: isDark ? APP_SEMANTIC.warning.light : APP_SEMANTIC.warning.dark,
          },
          standardInfo: {
            backgroundColor: alpha(APP_SEMANTIC.info.main, isDark ? 0.14 : 0.10),
            color: isDark ? APP_SEMANTIC.info.light : APP_SEMANTIC.info.dark,
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
}
